/**
 * Reach events → sounds and score cues (M3 plan R11, §2.6.4; audio-direction §3, §9). Reads `core.events` with its
 * own cursor, like the combat router:
 * - Every mapped event (`reach-audio.json` `events`) plays its sound: `hull` for the player's own travel (charge,
 *   engage, drop, tunnel), `ui` for cockpit cues and alarms, `world` at the event's float64 position (falling back
 *   to the cockpit when there is none, or when it is too far to hear).
 * - `transitStart` re-keys the score to the destination region (`setRegion`), so the new key lands in the tunnel;
 *   `systemEnter` does the same for arrivals without a tunnel (a load, a respawn transfer). `transitEnd` queues the
 *   BRAAAM on the next bar.
 * - A cruise charge stops early when it is cancelled (`cruiseCharge` a = 1, or travel no longer `charging`); a
 *   deep-scan charge stops when the scan key is released (`scan.holdS` back to 0).
 * Allocation-free per call (D-014), except the rare region change (key and BRAAAM re-render).
 */
import { EVENT_MASK, firstReadable, type EventRing } from '../../combat/events'
import { REV, type ReachEventName } from '../../reach/events'
import type { ReachCore } from '../../reach/runtime/types'
import type { StingerKind } from '../music/conductor'
import { ROUTE, type Route } from '../engine'
import type { SoundSink } from '../router'
import { soundIndex } from '../sfxSchema'
import { PRIORITY, type Priority } from '../voices'
import type { ReachAudioConfig, ReachRoute } from './config'
import { regionMusicKey, systemSeedOf } from './music'

/** What the router plays through: the engine (or a recorder in tests). */
export interface ReachSoundSink extends SoundSink {
  stopVoice(slot: number, sound: number, when?: number): void
}

/** The part of the score the router drives (the music system; `setRegion` is optional on fakes). */
export interface ReachMusicSink {
  stinger(kind: StingerKind): void
  setRegion?(region: string, seed: number): void
}

export const REACH_ROUTE = Object.freeze({ hull: 0, ui: 1, world: 2 })
const ROUTE_CODE: Readonly<Record<ReachRoute, number>> = REACH_ROUTE
/** Cockpit route and voice priority per reach route: own-ship travel sounds rank with incoming hits. */
const SINK_ROUTE: readonly Route[] = [ROUTE.hull, ROUTE.ui, ROUTE.ui]
const PRIORITY_OF: readonly Priority[] = [PRIORITY.incoming, PRIORITY.alarm, PRIORITY.near]
const ANY = -0x8000

/** Codes of the REV table + 1 (codes start at 1). */
const CODES = Math.max(...Object.values(REV)) + 1

export interface ReachAudioRouter {
  /** Per REV code: the sound index (-1 = unmapped), gain, route (REACH_ROUTE), and the a / b filters (ANY = any). */
  readonly sound: Int16Array
  readonly gain: Float32Array
  readonly route: Uint8Array
  readonly matchA: Int32Array
  readonly matchB: Int32Array
  readonly chargeSound: number
  readonly scanSound: number
  /** The live cruise-charge and scan-charge voices (-1 = none). */
  chargeSlot: number
  scanSlot: number
  /** Ring cursor. */
  cursor: number
}

/** Resolves the event map to runtime sound indices. Throws naming the event when a sound is not in the bank. */
export function createReachAudioRouter(cfg: ReachAudioConfig): ReachAudioRouter {
  const sound = new Int16Array(CODES).fill(-1)
  const gain = new Float32Array(CODES)
  const route = new Uint8Array(CODES)
  const matchA = new Int32Array(CODES).fill(ANY)
  const matchB = new Int32Array(CODES).fill(ANY)
  for (const name of Object.keys(cfg.events) as ReachEventName[]) {
    const m = cfg.events[name]
    if (m === undefined) continue
    const code = REV[name]
    const s = soundIndex(m.sound)
    if (s < 0) throw new Error(`reach audio: event ${name} maps to "${m.sound}", which is not in the sound bank`)
    sound[code] = s
    gain[code] = m.gain
    route[code] = ROUTE_CODE[m.route]
    if (m.a !== undefined) matchA[code] = m.a
    if (m.b !== undefined) matchB[code] = m.b
  }
  const charge = cfg.events.cruiseCharge
  const scan = cfg.events.scanCharge
  return {
    sound,
    gain,
    route,
    matchA,
    matchB,
    chargeSound: charge === undefined ? -1 : soundIndex(charge.sound),
    scanSound: scan === undefined ? -1 : soundIndex(scan.sound),
    chargeSlot: -1,
    scanSlot: -1,
    cursor: 0,
  }
}

/** Skips every event emitted so far (audio not running, or paused). */
export function skipReachEvents(router: ReachAudioRouter, ring: EventRing): void {
  router.cursor = ring.head
}

/** Re-keys the score to system `index`'s region (rare: may allocate). */
export function keyScoreTo(core: Pick<ReachCore, 'galaxy' | 'session' | 'layoutOf'>, index: number, music: ReachMusicSink): void {
  const system = core.galaxy.systems[index]
  if (system === undefined || music.setRegion === undefined) return
  music.setRegion(regionMusicKey(system.region), systemSeedOf(core, index))
}

function playMapped(router: ReachAudioRouter, code: number, x: number, y: number, z: number, sink: ReachSoundSink): number {
  const s = router.sound[code]
  const g = router.gain[code]
  const r = router.route[code]
  if (r === REACH_ROUTE.world && (x !== 0 || y !== 0 || z !== 0)) {
    const slot = sink.playWorld(s, g, x, y, z, PRIORITY_OF[r])
    if (slot >= 0) return slot
  }
  return sink.play(s, g, SINK_ROUTE[r], PRIORITY_OF[r])
}

/**
 * Plays every new reach event and drives the score. `music` is null until the score has loaded. Returns the
 * number of plays attempted.
 */
export function routeReachEvents(router: ReachAudioRouter, core: ReachCore, sink: ReachSoundSink, music: ReachMusicSink | null): number {
  const ring = core.events
  let plays = 0
  for (let i = firstReadable(ring, router.cursor); i < ring.head; i++) {
    const slot = i & EVENT_MASK
    const code = ring.type[slot]
    const a = ring.eid[slot]
    const b = ring.other[slot]
    switch (code) {
      case REV.cruiseCharge:
        if (a === 1) stopCharge(router, sink)
        break
      case REV.massLockDenied:
        stopCharge(router, sink)
        break
      case REV.cruiseEngage:
        router.chargeSlot = -1 // a completed charge rings out under the engage
        break
      case REV.scanPing:
        router.scanSlot = -1
        break
      case REV.transitStart:
        if (music !== null) keyScoreTo(core, b, music)
        break
      case REV.systemEnter:
        if (music !== null) keyScoreTo(core, a, music)
        break
      case REV.transitEnd:
        if (music !== null) music.stinger('braaam')
        break
      default:
        break
    }
    if (code <= 0 || code >= CODES || router.sound[code] < 0) continue
    if (router.matchA[code] !== ANY && router.matchA[code] !== a) continue
    if (router.matchB[code] !== ANY && router.matchB[code] !== b) continue
    const voice = playMapped(router, code, ring.x[slot], ring.y[slot], ring.z[slot], sink)
    if (code === REV.cruiseCharge) router.chargeSlot = voice
    else if (code === REV.scanCharge) router.scanSlot = voice
    plays++
  }
  router.cursor = ring.head
  // State-driven cancels: a charge released early, or cancelled in a tick whose event this frame already passed.
  if (router.chargeSlot >= 0 && core.travel.mode !== 'charging') stopCharge(router, sink)
  if (router.scanSlot >= 0 && core.scan.holdS <= 0) {
    sink.stopVoice(router.scanSlot, router.scanSound)
    router.scanSlot = -1
  }
  return plays
}

function stopCharge(router: ReachAudioRouter, sink: ReachSoundSink): void {
  if (router.chargeSlot < 0) return
  sink.stopVoice(router.chargeSlot, router.chargeSound)
  router.chargeSlot = -1
}
